import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, Plus, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FullPageSpinner } from '@/components/etainabl-spinner';
import { useReporters } from '@/queries/tickets';
import {
  useAdminEdition,
  useApproveEdition,
  useEditionPreview,
  useGuideOptions,
  useSaveEdition,
  useWithdrawEdition,
  type PreviewMode
} from '@/queries/releaseNotes';
import { CUSTOMER_AREAS } from '@/lib/productAreas';
import { cn } from '@/lib/utils';
import type { AdminEdition } from '@/types/releaseNotes';
import { EditionArticle } from '../EditionArticle';
import { periodLabel, publishTimeLabel } from '../format';
import { EditionStatusBadge } from './EditionStatusBadge';
import { HighlightsEditor } from './HighlightsEditor';
import { ItemEditor, selectClass } from './ItemEditor';
import {
  blankItem,
  draftFromEdition,
  isDirty,
  moveItem,
  remainingExcluded,
  removeItem,
  restoredItem,
  saveBody,
  updateItem,
  type EditorDraft
} from './editorState';

const errorProblems = (err: unknown): string[] => {
  const data = (err as { data?: { problems?: unknown } })?.data;
  return Array.isArray(data?.problems) ? (data.problems as string[]) : [];
};

const errorMessage = (err: unknown) => (err as { data?: { message?: string } })?.data?.message ?? (err as Error)?.message ?? 'Something went wrong';

function Preview({ editionKey, dirty }: { editionKey: string; dirty: boolean }) {
  const [mode, setMode] = useState<PreviewMode>('everyone');
  const [asUserId, setAsUserId] = useState('');
  const reporters = useReporters();
  const preview = useEditionPreview(editionKey, mode, asUserId || undefined);
  const modes: { value: PreviewMode; label: string }[] = [
    { value: 'everyone', label: 'Everyone' },
    { value: 'partner', label: 'Partner admin' },
    { value: 'user', label: 'A user' }
  ];
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div role="radiogroup" aria-label="Preview as" className="inline-flex rounded-etn-pill border border-etn-border-input bg-etn-surface p-[3px]">
          {modes.map(m => (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={mode === m.value}
              onClick={() => setMode(m.value)}
              className={cn(
                'h-7 rounded-etn-pill px-3.5 text-etn-control-sm font-semibold transition-colors',
                mode === m.value ? 'bg-etn-accent-tint text-etn-accent-text-active' : 'text-etn-text-label hover:text-etn-text'
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
        {mode === 'user' && (
          <div className="min-w-64 flex-1 space-y-1.5">
            <Label htmlFor="preview-user">User</Label>
            <select id="preview-user" className={selectClass} value={asUserId} onChange={e => setAsUserId(e.target.value)}>
              <option value="">Choose someone…</option>
              {(reporters.data ?? []).map(r => (
                <option key={r._id} value={r._id}>
                  {r.name || r.email} · {r.companyName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {dirty && <p className="text-etn-cell text-etn-warning-text">The preview shows the last saved version. Save to see your changes.</p>}
      {preview.data?.viewingAs && (
        <p className="text-etn-cell text-etn-text-label">
          As {preview.data.viewingAs.name} at {preview.data.viewingAs.companyName}
          {preview.data.viewingAs.isCompanyAdmin ? ', a Company Admin' : ''}.
        </p>
      )}
      {preview.isLoading && <p className="text-etn-cell text-etn-text-label">Loading preview…</p>}
      {preview.data && !preview.data.edition && (
        <p className="rounded-etn-card bg-etn-surface-sunken px-5 py-6 text-etn-cell text-etn-text-label">
          Nothing in this edition is shown to this reader, so it won’t appear in their list.
        </p>
      )}
      {preview.data?.edition && (
        <div className="rounded-etn-card border border-etn-border-hairline p-6 md:p-8">
          <EditionArticle edition={preview.data.edition} linkGuides={false} />
        </div>
      )}
    </div>
  );
}

function Editor({ edition }: { edition: AdminEdition }) {
  const saved = useMemo(() => draftFromEdition(edition), [edition]);
  const [draft, setDraft] = useState<EditorDraft>(saved);
  const [problems, setProblems] = useState<string[]>(edition.problems);
  const [tab, setTab] = useState('notes');
  const guides = useGuideOptions();
  const save = useSaveEdition(edition.key);
  const approve = useApproveEdition(edition.key);
  const withdraw = useWithdrawEdition(edition.key);
  const dirty = isDirty(draft, saved);
  const leftOut = remainingExcluded(edition.excluded, draft);
  const summaries = new Map(edition.excluded.map(e => [e._id, e.summary]));
  const busy = save.isPending || approve.isPending || withdraw.isPending;

  useEffect(() => {
    setDraft(saved);
    setProblems(edition.problems);
  }, [saved, edition.problems]);

  // Unsaved edits are easy to lose on a long review; the browser asks before leaving.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const run = async (action: () => Promise<AdminEdition>, success: string) => {
    try {
      const next = await action();
      setProblems(next.problems);
      toast.success(success);
    } catch (err) {
      const listed = errorProblems(err);
      if (listed.length) setProblems(listed);
      toast.error(errorMessage(err));
    }
  };

  const goesLive =
    new Date(edition.publishAt) <= new Date() || edition.kind === 'backfill' ? 'straight away' : `at ${publishTimeLabel(edition.publishAt)}`;

  const lastArea = draft.items.at(-1)?.area ?? CUSTOMER_AREAS[0].key;

  return (
    <div className="space-y-6">
      <Link
        to="/admin/release-notes"
        className="inline-flex items-center gap-1.5 text-etn-control-sm font-semibold text-etn-text-label hover:text-etn-text"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        All editions
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="flex flex-wrap items-center gap-2.5 text-2xl font-semibold text-etn-text">
            {periodLabel(edition)}
            <EditionStatusBadge status={edition.status} publishAt={edition.publishAt} />
          </h1>
          <p className="text-etn-cell text-etn-text-label">
            {edition.status === 'approved'
              ? new Date(edition.publishAt) <= new Date()
                ? `Live since ${publishTimeLabel(edition.publishAt)}. Edits go live as soon as you save.`
                : `Goes live ${publishTimeLabel(edition.publishAt)}.`
              : edition.kind === 'weekly'
                ? `Due ${publishTimeLabel(edition.publishAt)}. Nothing is published until it is approved.`
                : 'A monthly round-up. It goes live when it is approved.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {dirty && (
            <Button type="button" size="sm" variant="ghost" onClick={() => setDraft(saved)} disabled={busy}>
              <Undo2 />
              Discard changes
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!dirty || busy}
            onClick={() => run(() => save.mutateAsync(saveBody(draft)), 'Saved')}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {edition.status === 'approved' ? (
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={busy}
              onClick={() => {
                if (confirm('Withdraw this edition? Readers will stop seeing it straight away.')) run(() => withdraw.mutateAsync(), 'Withdrawn');
              }}
            >
              Withdraw
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              disabled={dirty || busy}
              title={dirty ? 'Save your changes first' : undefined}
              onClick={() => {
                if (confirm(`Approve this edition? It goes live ${goesLive}.`)) run(() => approve.mutateAsync(), 'Approved');
              }}
            >
              {approve.isPending ? 'Approving…' : 'Approve'}
            </Button>
          )}
        </div>
      </header>

      {problems.length > 0 && (
        <div
          role="status"
          className="space-y-1.5 rounded-etn-card bg-etn-warning-subtle px-5 py-4 text-etn-cell text-etn-warning-text"
          data-testid="approval-problems"
        >
          <p className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            {edition.status === 'approved' ? 'Fix these to keep the edition live' : 'Before this can be approved'}
          </p>
          <ul className="list-disc space-y-0.5 pl-10">
            {problems.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList variant="line" className="w-full justify-start gap-5">
          <TabsTrigger value="notes">Notes ({draft.items.length})</TabsTrigger>
          <TabsTrigger value="highlights">Highlights ({draft.highlights.length}/3)</TabsTrigger>
          <TabsTrigger value="left-out">Left out ({leftOut.length})</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="notes" className="pt-6">
          {draft.items.length === 0 && <p className="pb-4 text-etn-cell text-etn-text-label">No notes yet. Add one, or restore a left-out change.</p>}
          <ol className="divide-y divide-etn-border-hairline">
            {draft.items.map((item, i) => (
              <ItemEditor
                key={item.key}
                item={item}
                index={i}
                count={draft.items.length}
                guides={guides.data ?? []}
                restoredSummary={item.restoredFrom ? summaries.get(item.restoredFrom) : undefined}
                defaultOpen={!item._id}
                onChange={patch => setDraft(d => updateItem(d, item.key, patch))}
                onMove={by => setDraft(d => moveItem(d, item.key, by))}
                onRemove={() => setDraft(d => removeItem(d, item.key))}
              />
            ))}
          </ol>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-4 border-dashed"
            onClick={() => setDraft(d => ({ ...d, items: [...d.items, blankItem(lastArea)] }))}
          >
            <Plus />
            Add a note
          </Button>
        </TabsContent>

        <TabsContent value="highlights" className="pt-6">
          <HighlightsEditor items={draft.items} highlights={draft.highlights} onChange={highlights => setDraft(d => ({ ...d, highlights }))} />
        </TabsContent>

        <TabsContent value="left-out" className="pt-6">
          <p className="mb-4 max-w-[65ch] text-etn-cell text-etn-text-label">
            Changes the generator kept out, and why. Restoring one adds an empty note for you to write up; the rule usually had a reason, so check
            before you do.
          </p>
          {leftOut.length === 0 ? (
            <p className="text-etn-cell text-etn-text-label">Nothing was left out.</p>
          ) : (
            <ul className="divide-y divide-etn-border-hairline" data-testid="left-out-list">
              {leftOut.map(change => (
                <li key={change._id} className="flex flex-wrap items-start justify-between gap-4 py-4">
                  <div className="min-w-0 max-w-[70ch] space-y-1">
                    <p className="text-sm font-semibold text-etn-text">{change.summary || '(no summary)'}</p>
                    <p className="flex flex-wrap items-center gap-2 text-etn-cell text-etn-text-cell">
                      <Badge variant="neutral">{change.rule}</Badge>
                      {change.reason}
                    </p>
                    <p className="flex flex-wrap gap-x-3 text-etn-micro text-etn-text-label">
                      {change.sources.prs.map(pr => (
                        <a
                          key={`${pr.repo}#${pr.number}`}
                          href={`https://github.com/etainabl/${pr.repo}/pull/${pr.number}`}
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          {pr.repo === 'platform' ? '' : pr.repo}#{pr.number}
                        </a>
                      ))}
                      {change.sources.linear.map(id => (
                        <a key={id} href={`https://linear.app/etainabl/issue/${id}`} target="_blank" rel="noreferrer" className="underline">
                          {id}
                        </a>
                      ))}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setDraft(d => ({ ...d, items: [...d.items, restoredItem(change, lastArea)] }));
                      setTab('notes');
                    }}
                  >
                    Restore as a note
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="preview" className="pt-6">
          <Preview editionKey={edition.key} dirty={dirty} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function ReleaseNoteEditor({ editionKey }: { editionKey: string }) {
  const { data, isLoading, error } = useAdminEdition(editionKey);
  if (isLoading) return <FullPageSpinner srLabel="Loading edition" />;
  if (error || !data) return <p className="text-sm text-etn-text-label">Edition not found.</p>;
  return <Editor edition={data} />;
}
